import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'

import { EMPTY, Observable, catchError, map, switchMap, take } from 'rxjs'

import { nodeDocumentationTarget } from '@flowx/designer/modules/bpmn-editor/utils/node-documentation.util'
import { areaRootTemplateConfig } from '@flowx/designer/modules/process-details/utils/navigation-areas/area-root-template-config.util'
import { NodeApiService } from '@flowx/designer/shared/api/node.api'
import { SavedResourceDescription } from '@flowx/designer/shared/api/resource-description.api'
import { ResourceDocumentationService } from '@flowx/designer/shared/components/resource-documentation/resource-documentation.service'
import { ApplicationResourceType } from '@flowx/designer/shared/entities/application.entity'
import { Area, AreaPlatform, NavigationType } from '@flowx/designer/shared/entities/nav-area.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { selectNodes } from '@flowx/designer/shared/store/selectors/node.selectors'

export const AREA_DOCUMENTATION_FAILURE = 'Failed to open the area documentation'

export type AreaDocumentationContext = {
  platform: AreaPlatform
  swimlaneId: string
  processResourceId: string
  readOnly: boolean
}

// A user task area shows its user task; the parent process and embedded subprocess areas have none.
export const hasAreaDocumentation = (type: NavigationType): boolean =>
  type !== NavigationType.PARENT_PROCESS && type !== NavigationType.SUBPROCESS_EMBED

@Injectable({ providedIn: 'root' })
export class AreaDocumentationService {
  #nodeApi = inject(NodeApiService)
  #store = inject(Store)
  #resourceDocumentation = inject(ResourceDocumentationService)
  #toastMessageService = inject(ToastMessageService)

  open(
    area: Pick<Area, 'id' | 'label' | 'type' | 'nodeId'>,
    context: AreaDocumentationContext
  ): Observable<SavedResourceDescription> {
    const documentation =
      area.type === NavigationType.VIEWPORT
        ? this.#openUserTask(area.nodeId, context)
        : this.#openNavigationArea(area, context)

    return documentation.pipe(
      catchError(() => {
        this.#toastMessageService.showErrorMessage(AREA_DOCUMENTATION_FAILURE)

        return EMPTY
      })
    )
  }

  // The same element the process designer and the UI designer document for the user task.
  #openUserTask(
    nodeId: number,
    { processResourceId, readOnly }: AreaDocumentationContext
  ): Observable<SavedResourceDescription> {
    return this.#store.select(selectNodes).pipe(
      take(1),
      map((nodes) => nodes.find(({ id }) => id === nodeId)),
      switchMap((node) => {
        if (!node) {
          throw new Error('The user task of the area is not loaded')
        }

        return this.#resourceDocumentation.open(
          nodeDocumentationTarget(node, processResourceId, readOnly)
        )
      })
    )
  }

  // Same element as the UI designer's Documentation on the area, so both show one text; the root
  // UI template differs per platform.
  #openNavigationArea(
    area: Pick<Area, 'id' | 'label'>,
    { platform, swimlaneId, processResourceId, readOnly }: AreaDocumentationContext
  ): Observable<SavedResourceDescription> {
    return this.#nodeApi.getNavigationAreaTemplateConfigs(area.id, platform, swimlaneId).pipe(
      map((navigationTree) => areaRootTemplateConfig(navigationTree, area.id)),
      switchMap((rootTemplateConfig) => {
        if (!rootTemplateConfig?.flowxUuid) {
          throw new Error('The area has no UI template to document')
        }

        return this.#resourceDocumentation.open({
          resourceType: ApplicationResourceType.Process,
          resourceId: processResourceId,
          resourceName: area.label.trim(),
          readOnly,
          element: { elementType: 'uiTemplate', elementId: rootTemplateConfig.flowxUuid },
        })
      })
    )
  }
}
